import type {
  ChoiceKey,
  QuestionBankQuestion,
  QuestionImage,
  RichContentBlock,
  RichContentDocument,
  RichContentTextBlock,
  RichContentTextSegment,
} from "../types/question-bank";

const EMPTY_SEGMENT: RichContentTextSegment = { text: "" };
const SUPERSCRIPT_CHAR_MAP: Record<string, string> = {
  "0": "⁰",
  "1": "¹",
  "2": "²",
  "3": "³",
  "4": "⁴",
  "5": "⁵",
  "6": "⁶",
  "7": "⁷",
  "8": "⁸",
  "9": "⁹",
  "+": "⁺",
  "-": "⁻",
  "=": "⁼",
  "(": "⁽",
  ")": "⁾",
  a: "ᵃ",
  b: "ᵇ",
  c: "ᶜ",
  d: "ᵈ",
  e: "ᵉ",
  f: "ᶠ",
  g: "ᵍ",
  h: "ʰ",
  n: "ⁿ",
  i: "ⁱ",
  j: "ʲ",
  k: "ᵏ",
  l: "ˡ",
  m: "ᵐ",
  o: "ᵒ",
  p: "ᵖ",
  r: "ʳ",
  s: "ˢ",
  t: "ᵗ",
  u: "ᵘ",
  v: "ᵛ",
  w: "ʷ",
  x: "ˣ",
  y: "ʸ",
  z: "ᶻ",
};
const SUBSCRIPT_CHAR_MAP: Record<string, string> = {
  "0": "₀",
  "1": "₁",
  "2": "₂",
  "3": "₃",
  "4": "₄",
  "5": "₅",
  "6": "₆",
  "7": "₇",
  "8": "₈",
  "9": "₉",
  "+": "₊",
  "-": "₋",
  "=": "₌",
  "(": "₍",
  ")": "₎",
  a: "ₐ",
  e: "ₑ",
  h: "ₕ",
  i: "ᵢ",
  j: "ⱼ",
  k: "ₖ",
  l: "ₗ",
  m: "ₘ",
  n: "ₙ",
  o: "ₒ",
  p: "ₚ",
  r: "ᵣ",
  s: "ₛ",
  t: "ₜ",
  u: "ᵤ",
  v: "ᵥ",
  x: "ₓ",
};

const buildId = (prefix: string) => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
};

const collapseEscapedLatexPrefixes = (value: string) =>
  String(value || "")
    .replace(/\\\\(?=[A-Za-z()[\]{}$])/g, "\\")
    .replace(/\\r\\n/g, "\n");

const unwrapOuterParentheses = (value: string) => {
  let normalized = String(value || "").trim();
  let changed = true;

  while (changed && normalized.startsWith("(") && normalized.endsWith(")")) {
    changed = false;
    let depth = 0;
    let wrapsWholeValue = true;

    for (let index = 0; index < normalized.length; index += 1) {
      const char = normalized[index];
      if (char === "(") depth += 1;
      if (char === ")") depth -= 1;
      if (depth === 0 && index < normalized.length - 1) {
        wrapsWholeValue = false;
        break;
      }
    }

    if (wrapsWholeValue) {
      normalized = normalized.slice(1, -1).trim();
      changed = true;
    }
  }

  return normalized;
};

const normalizeScriptGrouping = (value: string) =>
  String(value || "").replace(/([_^])\s*(\(+)\s*([A-Za-z0-9+\-=]+)\s*(\)+)/g, (_match, marker, _open, inner) => {
    const normalizedInner = String(inner || "").trim();
    if (!normalizedInner) return marker;
    return /^[A-Za-z0-9]+$/.test(normalizedInner)
      ? `${marker}${normalizedInner}`
      : `${marker}(${normalizedInner})`;
  });

const normalizeLogicOperators = (value: string) =>
  String(value || "")
    .replace(/([A-Za-z0-9)])\s*\\?(?:Rightarrow|implies)\s*([A-Za-z0-9(])/g, "$1 ⇒ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:Leftrightarrow|iff)\s*([A-Za-z0-9(])/g, "$1 ⇔ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:vee|lor)\s*([A-Za-z0-9(])/g, "$1 ∨ $2")
    .replace(/([A-Za-z0-9)])\s*\\?(?:wedge|land)\s*([A-Za-z0-9(])/g, "$1 ∧ $2")
    .replace(/\\?neg\s*([A-Za-z0-9(])/g, "¬$1");

const normalizeGeneratedQuestionLayout = (value: string) => {
  let normalized = normalizeLogicOperators(normalizeScriptGrouping(String(value || "")))
    .replace(/&lt;br\s*\/?&gt;/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/\\n(?![A-Za-z])/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .replace(/[ \t]*\n[ \t]*/g, "\n")
    .replace(/(^|[^\n])\s*(?:\\bullet|•)\s+/g, (_match, prefix) => `${prefix}\n• `)
    .replace(/\b(Buna göre)\s+(I{1,3}|IV|V|VI|VII|VIII|IX|X)\.\s+/gi, (_match, prefix, numeral) => `${prefix}\n${numeral}. `)
    .replace(/([?:;])\s+(I{1,3}|IV|V|VI|VII|VIII|IX|X)\.\s+/g, (_match, prefix, numeral) => `${prefix}\n${numeral}. `)
    .replace(/([a-zçğıöşü0-9])\.\s+(II|III|IV|V|VI|VII|VIII|IX|X)\.\s+/g, (_match, prefix, numeral) => `${prefix}.\n${numeral}. `)
    .replace(/\n{3,}/g, "\n\n");

  normalized = normalized
    .split("\n")
    .map((line) => line.trim())
    .join("\n")
    .trim();

  return normalized;
};

export const createRichTextBlock = (text = ""): RichContentTextBlock => ({
  id: buildId("rich_text"),
  type: "text",
  segments: text ? [{ text: normalizeLatexText(text) }] : [{ ...EMPTY_SEGMENT }],
});

export const createRichImageBlock = (image: QuestionImage): RichContentBlock => ({
  id: buildId("rich_image"),
  type: "image",
  image: {
    ...image,
    prompt: typeof image.prompt === "string" ? image.prompt : "",
    trimBottomRatio: Number.isFinite(Number(image.trimBottomRatio)) ? Number(image.trimBottomRatio) : 0,
    scale: Number.isFinite(Number(image.scale)) ? Number(image.scale) : 1,
  },
});

export const createRichContentDocument = (text = ""): RichContentDocument => ({
  version: 1,
  blocks: [createRichTextBlock(text)],
});

const mapScriptText = (
  value: string,
  charMap: Record<string, string>,
  fallbackPrefix: "^" | "_"
) => {
  const normalized = unwrapOuterParentheses(String(value || "").replace(/[{}]/g, "").trim());
  if (!normalized) return "";

  const canConvertDirectly = normalized.split("").every((char) => Boolean(charMap[char]));
  if (canConvertDirectly) {
    return normalized
      .split("")
      .map((char) => charMap[char])
      .join("");
  }

  return `${fallbackPrefix}${normalized}`;
};

const normalizeLatexText = (value: string) => {
  let normalized = normalizeGeneratedQuestionLayout(collapseEscapedLatexPrefixes(value));
  if (!normalized) return "";
  if (!/[\\$^_{}]/.test(normalized)) return normalized;

  normalized = normalized
    .replace(/\r\n/g, "\n")
    .replace(/\\\$/g, "$")
    .replace(/\\\(/g, "(")
    .replace(/\\\)/g, ")")
    .replace(/\\\[/g, "[")
    .replace(/\\\]/g, "]")
    .replace(/\$\$/g, "")
    .replace(/\$/g, "")
    .replace(/\\dfrac/g, "\\frac")
    .replace(/\\tfrac/g, "\\frac")
    .replace(/\\left/g, "")
    .replace(/\\right/g, "");

  const unwrapCommands = (pattern: RegExp, replacer: string) => {
    let previous = "";
    while (previous !== normalized) {
      previous = normalized;
      normalized = normalized.replace(pattern, replacer);
    }
  };

  unwrapCommands(/\\text\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\mathrm\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\mathbf\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\mathit\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\operatorname\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\mathbb\{([A-Za-z])\}/g, "$1");
  unwrapCommands(/\\widehat\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\widetilde\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\hat\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\tilde\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\overline\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\overrightarrow\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\\vec\{([^{}]*)\}/g, "$1");
  // backslash düştüğünde (JSON parse'dan kaynaklanan sorun) — backslash'sız versiyonlar
  unwrapCommands(/\bwidehat\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\bwidetilde\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\boverline\{([^{}]*)\}/g, "$1");
  unwrapCommands(/\boverrightarrow\{([^{}]*)\}/g, "$1");

  normalized = normalized
    .replace(/\\cdot/g, "·")
    .replace(/\\times/g, "×")
    .replace(/\\div/g, "÷")
    .replace(/\\pm/g, "±")
    .replace(/\\mp/g, "∓")
    .replace(/\\leq?/g, "≤")
    .replace(/\\geq?/g, "≥")
    .replace(/\\neq/g, "≠")
    .replace(/\\approx/g, "≈")
    .replace(/\\cdots/g, "...")
    .replace(/\\ldots/g, "...")
    .replace(/\\infty/g, "∞")
    .replace(/\\pi/g, "π")
    .replace(/\\theta/g, "θ")
    .replace(/\\alpha/g, "α")
    .replace(/\\beta/g, "β")
    .replace(/\\gamma/g, "γ")
    .replace(/\\lambda/g, "λ")
    .replace(/\\mu/g, "μ")
    .replace(/\\sigma/g, "σ")
    .replace(/\\Delta/g, "Δ")
    .replace(/\\degree/g, "°")
    .replace(/\^\{?\\?circ\}?/g, "°")
    .replace(/\\circ\b/g, "∘")
    .replace(/\\omega/g, "ω")
    .replace(/\\Omega/g, "Ω")
    .replace(/\\phi/g, "φ")
    .replace(/\\psi/g, "ψ")
    .replace(/\\epsilon/g, "ε")
    .replace(/\\rho/g, "ρ")
    .replace(/\\eta/g, "η")
    .replace(/\\xi/g, "ξ")
    .replace(/\\zeta/g, "ζ")
    .replace(/\\kappa/g, "κ")
    .replace(/\\nu/g, "ν")
    .replace(/\\tau/g, "τ")
    .replace(/\\upsilon/g, "υ")
    .replace(/\\chi/g, "χ")
    .replace(/\\Gamma/g, "Γ")
    .replace(/\\Lambda/g, "Λ")
    .replace(/\\Sigma/g, "Σ")
    .replace(/\\Theta/g, "Θ")
    .replace(/\\Pi/g, "Π")
    .replace(/\\sum\b/g, "Σ")
    .replace(/\\prod\b/g, "Π")
    .replace(/\\forall/g, "∀")
    .replace(/\\exists/g, "∃")
    .replace(/\\emptyset/g, "∅")
    .replace(/\\varnothing/g, "∅")
    .replace(/\\lfloor/g, "⌊")
    .replace(/\\rfloor/g, "⌋")
    .replace(/\\lceil/g, "⌈")
    .replace(/\\rceil/g, "⌉")
    .replace(/\\sim\b/g, "~")
    .replace(/\\cong/g, "≅")
    .replace(/\\equiv/g, "≡")
    .replace(/\\propto/g, "∝")
    .replace(/\\mid\b/g, "|")
    .replace(/\\nmid/g, "∤")
    .replace(/\\in\b/g, "∈")
    .replace(/\\notin/g, "∉")
    .replace(/\\subseteq/g, "⊆")
    .replace(/\\supseteq/g, "⊇")
    .replace(/\\subset/g, "⊂")
    .replace(/\\supset/g, "⊃")
    .replace(/\\cup/g, "∪")
    .replace(/\\cap/g, "∩")
    .replace(/\\parallel/g, "∥")
    .replace(/\\perp/g, "⊥")
    .replace(/\\angle/g, "∠")
    .replace(/\\triangle/g, "△")
    .replace(/\\Rightarrow/g, "⇒")
    .replace(/\\implies/g, "⇒")
    .replace(/\\Leftrightarrow/g, "⇔")
    .replace(/\\iff/g, "⇔")
    .replace(/\\vee/g, "∨")
    .replace(/\\lor/g, "∨")
    .replace(/\\wedge/g, "∧")
    .replace(/\\land/g, "∧")
    .replace(/\\neg/g, "¬")
    .replace(/\\rightarrow/g, "→")
    .replace(/\\to/g, "→")
    .replace(/\\leftrightarrow/g, "↔")
    .replace(/\\prime/g, "′")
    .replace(/\\sqrt\(([^()\n]+)\)/g, "√($1)");

  const formatFractionPart = (part: string) => {
    const nextPart = normalizeLatexText(part);
    return /[+\-−=×÷· ]/.test(nextPart) ? `(${nextPart})` : nextPart;
  };

  let previous = "";
  while (previous !== normalized) {
    previous = normalized;
    normalized = normalized
      .replace(/\\frac\s*\{([^{}]+)\}\s*\{([^{}]+)\}/g, (_match, numerator, denominator) => `${formatFractionPart(numerator)}/${formatFractionPart(denominator)}`)
      .replace(/\\sqrt\[(.*?)\]\s*\{([^{}]+)\}/g, (_match, index, radicand) => `${normalizeLatexText(index)}√(${normalizeLatexText(radicand)})`)
      .replace(/\\sqrt\s*\{([^{}]+)\}/g, (_match, radicand) => `√(${normalizeLatexText(radicand)})`);
  }

  normalized = normalized
    .replace(/\^\{([^{}]+)\}/g, (_match, exponent) => mapScriptText(exponent, SUPERSCRIPT_CHAR_MAP, "^"))
    .replace(/\^([A-Za-z0-9+\-()]+)/g, (_match, exponent) => mapScriptText(exponent, SUPERSCRIPT_CHAR_MAP, "^"))
    .replace(/_\{([^{}]+)\}/g, (_match, subscript) => mapScriptText(subscript, SUBSCRIPT_CHAR_MAP, "_"))
    .replace(/_([A-Za-z0-9+\-()]+)/g, (_match, subscript) => mapScriptText(subscript, SUBSCRIPT_CHAR_MAP, "_"))
    .replace(/\\,/g, " ")
    .replace(/\\;/g, " ")
    .replace(/\\:/g, " ")
    .replace(/\\!/g, "")
    .replace(/\\newline/g, "\n")
    .replace(/\\\\/g, "\n")
    .replace(/\\([A-Za-z]+)\b/g, "$1")
    .replace(/[{}]/g, "")
    .replace(/\b(?:widehat|widetilde|overline|overrightarrow)([A-Za-z0-9]{1,6})\b/g, "$1")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/[ \t]{2,}/g, " ");

  return normalized.trim();
};

const normalizeSegments = (segments: RichContentTextSegment[] | undefined): RichContentTextSegment[] => {
  if (!Array.isArray(segments) || !segments.length) {
    return [EMPTY_SEGMENT];
  }

  const normalized = segments.reduce<RichContentTextSegment[]>((acc, segment) => {
    const rawText = typeof segment?.text === "string" ? segment.text : "";
    const normalizedText = normalizeLatexText(rawText);
    // normalizeLatexText içindeki .trim() kasıtlı satır sonlarını da siliyor; geri ekle
    const text = rawText.endsWith("\n") && !normalizedText.endsWith("\n") ? normalizedText + "\n" : normalizedText;
    const bold = Boolean(segment?.bold);
    const italic = Boolean(segment?.italic);
    const underline = Boolean(segment?.underline);
    const previous = acc[acc.length - 1];

    if (
      previous &&
      Boolean(previous.bold) === bold &&
      Boolean(previous.italic) === italic &&
      Boolean(previous.underline) === underline
    ) {
      previous.text += text;
      return acc;
    }

    if (text.length || acc.length) {
      acc.push({ text, bold: bold || undefined, italic: italic || undefined, underline: underline || undefined });
    } else {
      acc.push({ ...EMPTY_SEGMENT });
    }
    return acc;
  }, []);

  if (!normalized.length) {
    return [EMPTY_SEGMENT];
  }

  return normalized;
};

const normalizeImage = (image: QuestionImage | undefined | null): QuestionImage | null => {
  if (!image || typeof image !== "object") {
    return null;
  }

  const mimeType = typeof image.mimeType === "string" ? image.mimeType : "";
  const base64 = typeof image.base64 === "string" ? image.base64 : "";

  if (!mimeType || !base64) {
    return null;
  }

  return {
    mimeType,
    base64,
    prompt: typeof image.prompt === "string" ? image.prompt : "",
    fileName: typeof image.fileName === "string" && image.fileName.trim() ? image.fileName : undefined,
    trimBottomRatio: Number.isFinite(Number(image.trimBottomRatio)) ? Number(image.trimBottomRatio) : 0,
    scale: Number.isFinite(Number(image.scale)) ? Number(image.scale) : 1,
  };
};

export const normalizeRichContentDocument = (
  document: RichContentDocument | null | undefined,
  fallbackText = ""
): RichContentDocument => {
  if (!document || typeof document !== "object" || !Array.isArray(document.blocks)) {
    return createRichContentDocument(fallbackText);
  }

  const blocks = document.blocks.reduce<RichContentBlock[]>((acc, block) => {
    if (block?.type === "image") {
      const image = normalizeImage(block.image);
      if (image) {
        acc.push({
          id: typeof block.id === "string" && block.id.trim() ? block.id : buildId("rich_image"),
          type: "image",
          image,
        });
      }
      return acc;
    }

    if (block?.type === "text") {
      acc.push({
        id: typeof block.id === "string" && block.id.trim() ? block.id : buildId("rich_text"),
        type: "text",
        segments: normalizeSegments(block.segments),
      });
    }

    return acc;
  }, []);

  return {
    version: 1,
    blocks: blocks.length ? blocks : [createRichTextBlock(fallbackText)],
  };
};

export const cloneRichContentDocument = (document: RichContentDocument | null | undefined): RichContentDocument =>
  normalizeRichContentDocument(document);

export const extractPlainTextFromRichContent = (document: RichContentDocument | null | undefined) =>
  normalizeRichContentDocument(document)
    .blocks.map((block) => {
      if (block.type === "image") {
        return "";
      }
      return block.segments.map((segment) => segment.text).join("");
    })
    .filter((value) => value.length > 0)
    .join("\n")
    .trim();

export const getChoiceContent = (question: QuestionBankQuestion, choice: ChoiceKey) =>
  normalizeRichContentDocument(question.choiceContents?.[choice] || null, question.siklar?.[choice] || "");

export const setChoiceContent = (
  question: QuestionBankQuestion,
  choice: ChoiceKey,
  document: RichContentDocument
): QuestionBankQuestion => {
  const normalizedDocument = normalizeRichContentDocument(document, question.siklar?.[choice] || "");
  const plainText = extractPlainTextFromRichContent(normalizedDocument);

  return {
    ...question,
    siklar: {
      ...question.siklar,
      [choice]: plainText,
    },
    choiceContents: {
      ...(question.choiceContents || {}),
      [choice]: normalizedDocument,
    },
  };
};

export const setQuestionContent = (
  question: QuestionBankQuestion,
  document: RichContentDocument
): QuestionBankQuestion => {
  const normalizedDocument = normalizeRichContentDocument(document, question.soru_metni || "");
  return {
    ...question,
    soru_metni: extractPlainTextFromRichContent(normalizedDocument),
    questionContent: normalizedDocument,
  };
};
